import { useState } from "react";
import { Plus, Trash, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DatePicker } from "../DatePicker";
import { fmtBRL } from "../../lib/format";

const input = "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2.5 sm:py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const label = "text-[10px] uppercase tracking-[0.12em] text-zinc-500 block mb-1";
const section = "font-display text-xs font-semibold text-indigo-300 uppercase tracking-wider border-b border-zinc-800 pb-1.5 mb-3 mt-6 first:mt-0";

const Fld = ({ l, value, onChange, type = "text", span = "", testid, placeholder = "" }) => (
  <div className={span}>
    <label className={label}>{l}</label>
    <input data-testid={testid} type={type} className={input} value={value ?? ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} />
  </div>
);

const SelFld = ({ l, value, onChange, options, testid, span = "" }) => (
  <div className={span}>
    <label className={label}>{l}</label>
    <select data-testid={testid} className={input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
      {options.map(([v, txt]) => <option key={v} value={v}>{txt}</option>)}
    </select>
  </div>
);

const ORIGEM_OPTS = [
  ["0", "0 — Nacional"], ["1", "1 — Estrangeira (importação direta)"], ["2", "2 — Estrangeira (mercado interno)"],
  ["3", "3 — Nacional (importação 40–70%)"], ["4", "4 — Nacional (processos básicos)"],
  ["5", "5 — Nacional (importação ≤40%)"], ["8", "8 — Nacional (importação >70%)"],
];
const IPI_OPTS = [
  ["53", "53 — Saída não tributada"], ["52", "52 — Saída isenta"], ["51", "51 — Saída alíquota zero"],
  ["54", "54 — Saída imune"], ["55", "55 — Saída com suspensão"], ["99", "99 — Outras saídas"],
];
const PIS_COFINS_OPTS = [
  ["07", "07 — Operação isenta da contribuição"], ["06", "06 — Alíquota zero"], ["08", "08 — Sem incidência"],
  ["09", "09 — Suspensão"], ["49", "49 — Outras operações de saída"],
];

const TimeFld = ({ l, value, onChange, testid }) => (
  <div>
    <label className={label}>{l}</label>
    <input data-testid={testid} inputMode="numeric" placeholder="HH:MM" maxLength={5} className={input}
      value={value ?? ""}
      onChange={(e) => {
        const dg = e.target.value.replace(/\D/g, "").slice(0, 4);
        onChange(dg.length > 2 ? `${dg.slice(0, 2)}:${dg.slice(2)}` : dg);
      }} />
  </div>
);

export const NfeForm = ({ form, setForm, ultimoNumero = 0 }) => {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDest = (k, v) => setForm((f) => ({ ...f, destinatario: { ...f.destinatario, [k]: v } }));

  const [cepBusy, setCepBusy] = useState(false);
  const handleDestCep = async (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    const masked = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    setDest("cep", masked);
    if (digits.length !== 8) return;
    setCepBusy(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data.erro) {
        toast.error("CEP não encontrado — preencha o endereço manualmente");
      } else {
        setForm((f) => ({
          ...f,
          destinatario: {
            ...f.destinatario, cep: masked,
            logradouro: data.logradouro || f.destinatario.logradouro,
            bairro: data.bairro || f.destinatario.bairro,
            municipio: data.localidade || f.destinatario.municipio,
            uf: (data.uf || f.destinatario.uf || "").toUpperCase(),
          },
        }));
        toast.success("Endereço preenchido pelo CEP");
      }
    } catch {
      toast.error("Não foi possível consultar o CEP — preencha manualmente");
    }
    setCepBusy(false);
  };
  const setImp = (k, v) => setForm((f) => ({ ...f, impostos: { ...f.impostos, [k]: v } }));
  const setTr = (k, v) => setForm((f) => ({ ...f, transportador: { ...f.transportador, [k]: v } }));
  const setVol = (k, v) => setForm((f) => ({ ...f, volumes: { ...f.volumes, [k]: v } }));

  const num = (v) => parseFloat(String(v ?? "").replace(",", ".")) || 0;

  const recalc = (itens, imp) => {
    const produtos = Math.round(itens.reduce((s, it) => s + num(it.quantidade) * num(it.valor_unitario), 0) * 100) / 100;
    const total = Math.round((produtos - num(imp.desconto) + num(imp.frete) + num(imp.seguro)
      + num(imp.outras_despesas) + num(imp.valor_ipi) + num(imp.valor_icms_st)) * 100) / 100;
    return { ...imp, valor_produtos: produtos, valor_total: total };
  };

  // Redistribui proporcionalmente (preserva entrada/parcelas desiguais); sem base anterior, divide igual. Último absorve arredondamento.
  const redistDups = (dups, total) => {
    const n = dups.length;
    if (n === 0 || total <= 0) return dups;
    const tc = Math.round(total * 100);
    const oldCents = dups.map((d) => Math.round((parseFloat(d.valor) || 0) * 100));
    const oldSum = oldCents.reduce((a, b) => a + b, 0);
    const cents = oldSum > 0 ? oldCents.map((c) => Math.round((c * tc) / oldSum)) : Array(n).fill(Math.round(tc / n));
    cents[n - 1] = tc - cents.slice(0, n - 1).reduce((a, b) => a + b, 0);
    return dups.map((d, i) => ({ ...d, valor: cents[i] / 100 }));
  };
  const somaDups = Math.round(form.duplicatas.reduce((s, d) => s + Math.round((parseFloat(d.valor) || 0) * 100), 0)) / 100;
  const totalNota = num(form.impostos?.valor_total);
  const dupsOk = form.duplicatas.length === 0 || Math.abs(somaDups - totalNota) < 0.01;

  const setItem = (idx, k, v) => setForm((f) => {
    const itens = f.itens.map((it, i) => {
      if (i !== idx) return it;
      const upd = { ...it, [k]: v };
      upd.valor_total = Math.round(num(upd.quantidade) * num(upd.valor_unitario) * 100) / 100;
      return upd;
    });
    const impostos = recalc(itens, f.impostos);
    return { ...f, itens, impostos, duplicatas: redistDups(f.duplicatas, num(impostos.valor_total)) };
  });
  const addItem = () => setForm((f) => ({
    ...f,
    itens: [...f.itens, { ...f.itens[f.itens.length - 1], _k: Math.random().toString(36).slice(2), codigo: String(f.itens.length + 1), descricao: "", quantidade: 1, valor_unitario: 0, valor_total: 0 }],
  }));
  const rmItem = (idx) => setForm((f) => {
    const itens = f.itens.filter((_, i) => i !== idx);
    const impostos = recalc(itens, f.impostos);
    return { ...f, itens, impostos, duplicatas: redistDups(f.duplicatas, num(impostos.valor_total)) };
  });
  const setImpRecalc = (k, v) => setForm((f) => {
    const imp = { ...f.impostos, [k]: v };
    const impostos = recalc(f.itens, imp);
    return { ...f, impostos, duplicatas: redistDups(f.duplicatas, num(impostos.valor_total)) };
  });

  const setDup = (idx, k, v) => setForm((f) => ({
    ...f, duplicatas: f.duplicatas.map((d, i) => (i === idx ? { ...d, [k]: v } : d)),
  }));
  const addDup = () => setForm((f) => ({
    ...f, duplicatas: [...f.duplicatas, { _k: Math.random().toString(36).slice(2), numero: "", vencimento: "", valor: 0 }],
  }));
  const rmDup = (idx) => setForm((f) => ({ ...f, duplicatas: f.duplicatas.filter((_, i) => i !== idx) }));

  return (
    <div data-testid="nfe-form" className="pb-24 sm:pb-0">
      <h3 className={section}>Nota</h3>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Fld l="Natureza da Operação" testid="nfe-natureza" span="sm:col-span-2" value={form.natureza_operacao} onChange={(v) => set("natureza_operacao", v)} />
        <div>
          <label className={label}>Número da NF-e</label>
          <input data-testid="nfe-numero-nota" className={input} value={form.numero_nota ?? ""} placeholder="Automático"
            onChange={(e) => set("numero_nota", e.target.value)} />
          {ultimoNumero > 0 && (
            <p data-testid="nfe-ultimo-numero-hint" className="text-[10px] leading-snug text-emerald-400/90 mt-1">
              Última nota emitida: <b>nº {ultimoNumero}</b> → próxima: <b>nº {form.numero_nota || ultimoNumero + 1}</b>
            </p>
          )}
        </div>
        <div>
          <label className={label}>Data de Emissão</label>
          <DatePicker testId="nfe-data-emissao" value={form.data_emissao} onChange={(v) => set("data_emissao", v)} />
        </div>
        <div>
          <label className={label}>Data Saída</label>
          <DatePicker testId="nfe-data-saida" value={form.data_saida} onChange={(v) => set("data_saida", v)} clearable />
        </div>
        <TimeFld l="Hora Saída" testid="nfe-hora-saida" value={form.hora_saida} onChange={(v) => set("hora_saida", v)} />
      </div>

      <h3 className={section}>Destinatário / Remetente</h3>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Fld l="Nome / Razão Social" testid="nfe-dest-nome" span="sm:col-span-2" value={form.destinatario.nome} onChange={(v) => setDest("nome", v)} />
        <Fld l="CNPJ / CPF" testid="nfe-dest-doc" value={form.destinatario.cnpj_cpf} onChange={(v) => setDest("cnpj_cpf", v)} />
        <Fld l="Inscrição Estadual" testid="nfe-dest-ie" value={form.destinatario.ie} onChange={(v) => setDest("ie", v)} />
        <Fld l="Endereço (rua)" testid="nfe-dest-logradouro" span="sm:col-span-2" value={form.destinatario.logradouro} onChange={(v) => setDest("logradouro", v)} />
        <Fld l="Número" testid="nfe-dest-numero" value={form.destinatario.numero} onChange={(v) => setDest("numero", v)} />
        <Fld l="Bairro / Distrito" testid="nfe-dest-bairro" value={form.destinatario.bairro} onChange={(v) => setDest("bairro", v)} />
        <div>
          <label className={label}>CEP</label>
          <div className="relative">
            <input data-testid="nfe-dest-cep" className={input} value={form.destinatario.cep ?? ""}
              placeholder="00000-000" inputMode="numeric" maxLength={9}
              onChange={(e) => handleDestCep(e.target.value)} />
            {cepBusy && (
              <CircleNotch size={15} data-testid="nfe-cep-spinner"
                className="animate-spin text-indigo-400 absolute right-2.5 top-1/2 -translate-y-1/2" />
            )}
          </div>
        </div>
        <Fld l="Município" testid="nfe-dest-municipio" value={form.destinatario.municipio} onChange={(v) => setDest("municipio", v)} />
        <Fld l="UF" testid="nfe-dest-uf" value={form.destinatario.uf} onChange={(v) => setDest("uf", v)} placeholder="SP" />
        <Fld l="Telefone / Fax" testid="nfe-dest-telefone" value={form.destinatario.telefone} onChange={(v) => setDest("telefone", v)} />
      </div>

      <h3 className={section}>Produtos / Serviços</h3>
      <div className="space-y-3">
        {form.itens.map((it, i) => (
          <div key={it._k || i} data-testid={`nfe-item-${i}`} className="border border-zinc-800 rounded-lg p-3 bg-zinc-900/40">
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-2.5">
              <Fld l="Código" value={it.codigo} onChange={(v) => setItem(i, "codigo", v)} />
              <Fld l="Descrição do Produto" testid={`nfe-item-desc-${i}`} span="sm:col-span-3" value={it.descricao} onChange={(v) => setItem(i, "descricao", v)} />
              <Fld l="NCM/SH" value={it.ncm} onChange={(v) => setItem(i, "ncm", v)} />
              <Fld l="CSOSN/CST" value={it.csosn} onChange={(v) => setItem(i, "csosn", v)} />
              <Fld l="CFOP" value={it.cfop} onChange={(v) => setItem(i, "cfop", v)} />
              <Fld l="Unidade" value={it.unidade} onChange={(v) => setItem(i, "unidade", v)} />
              <Fld l="Quantidade" type="number" testid={`nfe-item-qtd-${i}`} value={it.quantidade} onChange={(v) => setItem(i, "quantidade", v)} />
              <Fld l="Valor Unitário" type="number" testid={`nfe-item-vu-${i}`} value={it.valor_unitario} onChange={(v) => setItem(i, "valor_unitario", v)} />
              <Fld l="Valor Total" type="number" value={it.valor_total} onChange={(v) => setItem(i, "valor_total", v)} />
              <Fld l="Desconto" type="number" value={it.desconto} onChange={(v) => setItem(i, "desconto", v)} />
              <Fld l="B. Cálc. ICMS" type="number" value={it.bc_icms} onChange={(v) => setItem(i, "bc_icms", v)} />
              <Fld l="Valor ICMS" type="number" value={it.valor_icms} onChange={(v) => setItem(i, "valor_icms", v)} />
              <Fld l="Valor IPI" type="number" value={it.valor_ipi} onChange={(v) => setItem(i, "valor_ipi", v)} />
              <Fld l="Alíq. ICMS %" type="number" value={it.aliq_icms} onChange={(v) => setItem(i, "aliq_icms", v)} />
              <Fld l="Alíq. IPI %" type="number" value={it.aliq_ipi} onChange={(v) => setItem(i, "aliq_ipi", v)} />
              <div className="flex items-end">
                <button type="button" data-testid={`nfe-item-remove-${i}`} onClick={() => rmItem(i)}
                  disabled={form.itens.length === 1}
                  className="flex items-center gap-1 text-rose-400 hover:bg-rose-500/10 disabled:opacity-30 text-xs px-2.5 py-2 rounded transition-colors">
                  <Trash size={14} /> Remover
                </button>
              </div>
            </div>
            <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-4 gap-2.5">
              <SelFld l="Origem da Mercadoria" testid={`nfe-item-origem-${i}`} value={it.origem ?? "0"} options={ORIGEM_OPTS} onChange={(v) => setItem(i, "origem", v)} />
              <SelFld l="IPI (situação)" testid={`nfe-item-ipi-${i}`} value={it.ipi_st ?? "53"} options={IPI_OPTS} onChange={(v) => setItem(i, "ipi_st", v)} />
              <SelFld l="PIS (situação)" testid={`nfe-item-pis-${i}`} value={it.pis_st ?? "07"} options={PIS_COFINS_OPTS} onChange={(v) => setItem(i, "pis_st", v)} />
              <SelFld l="COFINS (situação)" testid={`nfe-item-cofins-${i}`} value={it.cofins_st ?? "07"} options={PIS_COFINS_OPTS} onChange={(v) => setItem(i, "cofins_st", v)} />
            </div>
          </div>
        ))}
        <button type="button" data-testid="nfe-add-item" onClick={addItem}
          className="flex items-center gap-1.5 text-indigo-400 hover:bg-indigo-500/10 text-xs font-medium px-3 py-2 rounded transition-colors">
          <Plus size={14} /> Adicionar produto
        </button>
      </div>

      <h3 className={section}>Duplicatas (Fatura)</h3>
      <div className="space-y-2">
        {form.duplicatas.map((d, i) => (
          <div key={d._k || i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2.5 sm:items-end border border-zinc-800 sm:border-0 rounded-lg p-3 sm:p-0">
            <div>
              <Fld l={d.entrada ? "Número · Entrada (paga no ato)" : "Número"} testid={`nfe-dup-num-${i}`} value={d.numero} onChange={(v) => setDup(i, "numero", v)} />
            </div>
            <div>
              <label className={label}>Vencimento</label>
              <DatePicker testId={`nfe-dup-venc-${i}`} value={d.vencimento} onChange={(v) => setDup(i, "vencimento", v)} />
            </div>
            <Fld l="Valor (R$)" type="number" value={d.valor} onChange={(v) => setDup(i, "valor", v)} />
            <button type="button" onClick={() => rmDup(i)}
              className="flex items-center justify-center gap-1.5 text-rose-400 hover:bg-rose-500/10 p-2 min-h-[40px] rounded transition-colors sm:mb-0.5">
              <Trash size={15} /><span className="sm:hidden text-xs">Remover duplicata</span>
            </button>
          </div>
        ))}
        {form.duplicatas.length > 0 && totalNota > 0 && (
          <p data-testid="nfe-dups-soma" className={`text-xs font-medium ${dupsOk ? "text-emerald-400" : "text-rose-400"}`}>
            {dupsOk ? `✓ Soma das duplicatas: ${fmtBRL(somaDups)} — bate com o total da nota` : `✕ Soma das duplicatas ${fmtBRL(somaDups)} difere do total da nota ${fmtBRL(totalNota)} — a SEFAZ rejeita; ajuste os valores`}
          </p>
        )}
        <button type="button" data-testid="nfe-add-dup" onClick={addDup}
          className="flex items-center gap-1.5 text-indigo-400 hover:bg-indigo-500/10 text-xs font-medium px-3 py-2 rounded transition-colors">
          <Plus size={14} /> Adicionar duplicata
        </button>
      </div>

      <h3 className={section}>Cálculo do Imposto</h3>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Fld l="Base de Cálc. do ICMS" type="number" value={form.impostos.bc_icms} onChange={(v) => setImp("bc_icms", v)} />
        <Fld l="Valor do ICMS" type="number" value={form.impostos.valor_icms} onChange={(v) => setImp("valor_icms", v)} />
        <Fld l="Base Cálc. ICMS Subst." type="number" value={form.impostos.bc_icms_st} onChange={(v) => setImp("bc_icms_st", v)} />
        <Fld l="Valor do ICMS Subst." type="number" value={form.impostos.valor_icms_st} onChange={(v) => setImpRecalc("valor_icms_st", v)} />
        <Fld l="Valor Aprox. Tributos" type="number" value={form.impostos.valor_tributos} onChange={(v) => setImp("valor_tributos", v)} />
        <Fld l="Valor do Frete" type="number" testid="nfe-imp-frete" value={form.impostos.frete} onChange={(v) => setImpRecalc("frete", v)} />
        <Fld l="Valor do Seguro" type="number" value={form.impostos.seguro} onChange={(v) => setImpRecalc("seguro", v)} />
        <Fld l="Desconto" type="number" testid="nfe-imp-desconto" value={form.impostos.desconto} onChange={(v) => setImpRecalc("desconto", v)} />
        <Fld l="Outras Desp. Acessórias" type="number" value={form.impostos.outras_despesas} onChange={(v) => setImpRecalc("outras_despesas", v)} />
        <Fld l="Valor do IPI" type="number" value={form.impostos.valor_ipi} onChange={(v) => setImpRecalc("valor_ipi", v)} />
        <Fld l="Valor Total dos Produtos" type="number" testid="nfe-imp-produtos" value={form.impostos.valor_produtos} onChange={(v) => setImp("valor_produtos", v)} />
        <Fld l="Valor Total da Nota" type="number" testid="nfe-imp-total" value={form.impostos.valor_total} onChange={(v) => setImp("valor_total", v)} />
      </div>

      <h3 className={section}>Transportador / Volumes Transportados</h3>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Fld l="Razão Social" span="sm:col-span-2" value={form.transportador.nome} onChange={(v) => setTr("nome", v)} />
        <div>
          <label className={label}>Frete por Conta</label>
          <select data-testid="nfe-tr-frete" className={input} value={form.transportador.frete_por_conta}
            onChange={(e) => setTr("frete_por_conta", e.target.value)}>
            <option value="0">0 - Emitente (Remetente)</option>
            <option value="1">1 - Destinatário</option>
            <option value="2">2 - Terceiros</option>
            <option value="9">9 - Sem Frete</option>
          </select>
        </div>
        <Fld l="Código ANTT" value={form.transportador.codigo_antt} onChange={(v) => setTr("codigo_antt", v)} />
        <Fld l="Placa do Veículo" value={form.transportador.placa} onChange={(v) => setTr("placa", v)} />
        <Fld l="UF da Placa" value={form.transportador.uf_placa} onChange={(v) => setTr("uf_placa", v)} />
        <Fld l="CNPJ / CPF" value={form.transportador.cnpj_cpf} onChange={(v) => setTr("cnpj_cpf", v)} />
        <Fld l="Inscrição Estadual" value={form.transportador.ie} onChange={(v) => setTr("ie", v)} />
        <Fld l="Endereço" span="sm:col-span-2" value={form.transportador.endereco} onChange={(v) => setTr("endereco", v)} />
        <Fld l="Município" value={form.transportador.municipio} onChange={(v) => setTr("municipio", v)} />
        <Fld l="UF" value={form.transportador.uf} onChange={(v) => setTr("uf", v)} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mt-3">
        <Fld l="Quantidade" value={form.volumes.quantidade} onChange={(v) => setVol("quantidade", v)} />
        <Fld l="Espécie" value={form.volumes.especie} onChange={(v) => setVol("especie", v)} placeholder="CHAPA" />
        <Fld l="Marca" value={form.volumes.marca} onChange={(v) => setVol("marca", v)} />
        <Fld l="Numeração" value={form.volumes.numeracao} onChange={(v) => setVol("numeracao", v)} />
        <Fld l="Peso Bruto" type="number" value={form.volumes.peso_bruto} onChange={(v) => setVol("peso_bruto", v)} />
        <Fld l="Peso Líquido" type="number" value={form.volumes.peso_liquido} onChange={(v) => setVol("peso_liquido", v)} />
      </div>

      <h3 className={section}>Dados Adicionais</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={label}>Informações Complementares</label>
          <textarea data-testid="nfe-info-compl" rows={3} className={`${input} resize-none`}
            placeholder="Ex: Vendedor responsável: Fulano — (11) 99999-9999"
            value={form.info_complementares ?? ""} onChange={(e) => set("info_complementares", e.target.value)} />
        </div>
        <div>
          <label className={label}>Reservado ao Fisco</label>
          <textarea data-testid="nfe-fisco" rows={3} className={`${input} resize-none`}
            value={form.reservado_fisco ?? ""} onChange={(e) => set("reservado_fisco", e.target.value)} />
        </div>
      </div>
    </div>
  );
};
